import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { ChevronLeft, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { usePreferences } from '../hooks/usePreferences'
import { useHouseholdRealtime } from '../hooks/useHouseholdRealtime'
import PriceHistoryChart from '../components/pantry/PriceHistoryChart'

// ─── Types ────────────────────────────────────────────────────────────────────

type SortMode = 'rising' | 'falling' | 'count' | 'alpha'

interface ItemHistory {
  itemId:          string
  itemName:        string
  recordCount:     number
  oldestPrice:     number
  latestPrice:     number
  priceChangePct:  number
  avgPrice:        number
}

// ─── Trend badge (warm-editorial palette) ─────────────────────────────────────

function TrendBadge({ pct }: { pct: number }) {
  const tone =
    pct >  2 ? 'bg-cream-warm text-terracotta' :
    pct < -2 ? 'bg-ok-bg text-forest' :
              'bg-paper text-ink-muted border border-border-sand'
  const Icon = pct > 2 ? ArrowUpRight : pct < -2 ? ArrowDownRight : Minus
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${tone}`}>
      <Icon className="h-3 w-3" strokeWidth={2.2} />
      {Math.abs(pct).toFixed(0)}%
    </span>
  )
}

// ─── Section eyebrow with dotted line + trailing meta ─────────────────────────

function SectionEyebrow({ label, meta }: { label: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 mt-6 mb-3">
      <h2 className="font-serif text-[14px] italic text-ink">{label}</h2>
      <div className="flex-1 h-px bg-border-sand" />
      {meta && (
        <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
          {meta}
        </span>
      )}
    </div>
  )
}

// ─── PriceHistory ─────────────────────────────────────────────────────────────

export default function PriceHistory() {
  const navigate    = useNavigate()
  const { session } = useAuth()
  const { isPremium } = usePreferences()
  const userId      = session?.user.id

  const [sort, setSort] = useState<SortMode>('rising')

  useEffect(() => { document.title = 'Price History — Pantry' }, [])

  // ── Household id ──────────────────────────────────────────────────────────

  const { data: userData } = useQuery({
    queryKey: ['userMeta', userId],
    enabled:  !!userId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', userId!)
        .single()
      return data
    },
  })

  const householdId = userData?.household_id ?? null

  useHouseholdRealtime(householdId, {
    tables: ['store_prices', 'confirmations'],
    invalidateMap: {
      store_prices:  [['store-prices', householdId], ['all-price-history', householdId], ['priceHistory']],
      confirmations: [['all-price-history', householdId]],
    },
  })

  // ── All price confirmations ───────────────────────────────────────────────

  const { data: allConfs = [], isLoading } = useQuery({
    queryKey: ['all-price-history', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select('item_id, price_paid, confirmed_at, pantry_items(name)')
        .eq('household_id', householdId!)
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: true })
      return data ?? []
    },
  })

  // ── Compute per-item summaries ────────────────────────────────────────────

  const itemHistories = useMemo<ItemHistory[]>(() => {
    const grouped: Record<string, { name: string; prices: { price: number; at: string }[] }> = {}
    for (const c of allConfs) {
      const id = c.item_id
      if (!grouped[id]) {
        const item = (Array.isArray(c.pantry_items) ? c.pantry_items[0] : c.pantry_items) as { name: string } | null
        grouped[id] = { name: item?.name ?? 'Unknown', prices: [] }
      }
      grouped[id].prices.push({ price: c.price_paid as number, at: c.confirmed_at })
    }

    return Object.entries(grouped)
      .filter(([, v]) => v.prices.length >= 2)
      .map(([id, v]) => {
        const sorted        = v.prices.slice().sort((a, b) => a.at.localeCompare(b.at))
        const oldestPrice   = sorted[0].price
        const latestPrice   = sorted[sorted.length - 1].price
        const allPrices     = sorted.map(p => p.price)
        const avgPrice      = allPrices.reduce((s, p) => s + p, 0) / allPrices.length
        const priceChangePct = oldestPrice > 0
          ? ((latestPrice - oldestPrice) / oldestPrice) * 100
          : 0
        return {
          itemId:        id,
          itemName:      v.name,
          recordCount:   v.prices.length,
          oldestPrice,
          latestPrice,
          priceChangePct,
          avgPrice,
        }
      })
  }, [allConfs])

  // ── Inflation stats ───────────────────────────────────────────────────────

  const risingCount  = itemHistories.filter(i => i.priceChangePct > 1).length
  const fallingCount = itemHistories.filter(i => i.priceChangePct < -1).length
  const stableCount  = itemHistories.length - risingCount - fallingCount

  const personalInflationRate = useMemo(() => {
    if (itemHistories.length === 0) return null
    return itemHistories.reduce((s, i) => s + i.priceChangePct, 0) / itemHistories.length
  }, [itemHistories])

  // ── Sort ──────────────────────────────────────────────────────────────────

  const sortedItems = useMemo(() => {
    const copy = [...itemHistories]
    switch (sort) {
      case 'rising':  return copy.sort((a, b) => b.priceChangePct - a.priceChangePct)
      case 'falling': return copy.sort((a, b) => a.priceChangePct - b.priceChangePct)
      case 'count':   return copy.sort((a, b) => b.recordCount - a.recordCount)
      case 'alpha':   return copy.sort((a, b) => a.itemName.localeCompare(b.itemName))
    }
  }, [itemHistories, sort])

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream pb-24">
        <div className="max-w-[440px] mx-auto px-5 pt-3">
          <div className="h-5 w-28 rounded-lg bg-paper border border-border-sand animate-pulse mb-2" />
          <div className="h-7 w-56 rounded-lg bg-paper border border-border-sand animate-pulse mb-6" />
          <div className="flex flex-col gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 rounded-2xl bg-paper border border-border-sand animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  const inflationTone = (personalInflationRate ?? 0) > 0 ? 'text-terracotta' : 'text-forest'

  return (
    <main className="min-h-screen bg-cream pb-24">
      <div className="max-w-[440px] mx-auto px-5">

        {/* Header */}
        <div className="pt-3 pb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1.5 rounded-full text-ink"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium">
              Price history
            </p>
            <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mt-0.5">
              Your grocery <em className="italic">inflation</em>
            </h1>
          </div>
        </div>

        {/* Inflation summary card */}
        {itemHistories.length > 0 && (
          <motion.div
            className="bg-paper border border-border-sand rounded-3xl p-5"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p className="text-[10px] uppercase tracking-[0.15em] text-ink-muted font-medium mb-3">
              Across {itemHistories.length} tracked item{itemHistories.length === 1 ? '' : 's'}
            </p>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center">
                <p className="font-serif text-[26px] font-medium text-terracotta tabular-nums leading-none">
                  {risingCount}
                </p>
                <p className="font-serif italic text-[11px] text-ink-muted mt-1">getting pricier</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-[26px] font-medium text-forest tabular-nums leading-none">
                  {fallingCount}
                </p>
                <p className="font-serif italic text-[11px] text-ink-muted mt-1">getting cheaper</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-[26px] font-medium text-ink-muted tabular-nums leading-none">
                  {stableCount}
                </p>
                <p className="font-serif italic text-[11px] text-ink-muted mt-1">stable</p>
              </div>
            </div>

            {personalInflationRate !== null && (
              <div className="rounded-2xl border border-border-hairline bg-cream-warm/40 p-3">
                <p className="text-[10px] uppercase tracking-[0.15em] text-ink-muted font-medium">
                  Personal inflation rate
                </p>
                <p className={`font-serif text-[18px] font-medium mt-0.5 tabular-nums ${inflationTone}`}>
                  {personalInflationRate > 0 ? '+' : ''}
                  {personalInflationRate.toFixed(1)}%
                  <span className="font-serif italic text-[12px] text-ink-muted ml-1.5 not-italic">
                    since you started tracking
                  </span>
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Sort pills */}
        {itemHistories.length > 0 && (
          <SectionEyebrow label={<>Sort by</>} />
        )}
        {itemHistories.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {([
              ['rising',  'Biggest increase'],
              ['falling', 'Biggest decrease'],
              ['count',   'Most records'],
              ['alpha',   'A–Z'],
            ] as [SortMode, string][]).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => setSort(mode)}
                className={`rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.08em] font-medium transition-colors ${
                  sort === mode
                    ? 'bg-forest text-forest-ink'
                    : 'bg-paper border border-border-sand text-ink-muted'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && itemHistories.length === 0 && (
          <div className="bg-paper border border-border-sand rounded-2xl px-5 py-10 text-center mt-6">
            <p className="font-serif italic text-[15px] text-ink">No price history yet.</p>
            <p className="font-serif text-[12px] italic text-ink-muted mt-1.5 max-w-[260px] mx-auto leading-[1.5]">
              Log prices when you confirm items to start tracking your personal grocery inflation.
            </p>
          </div>
        )}

        {/* Item cards */}
        {!isLoading && sortedItems.length > 0 && (
          <SectionEyebrow
            label={<>Every <em className="italic">item</em></>}
            meta={`${sortedItems.length} tracked`}
          />
        )}
        {!isLoading && sortedItems.map((item, idx) => (
          <motion.div
            key={item.itemId}
            className="bg-paper border border-border-sand rounded-2xl px-5 py-4 mb-3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04, duration: 0.22 }}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[15px] font-medium text-ink leading-tight truncate">
                  {item.itemName}
                </p>
                <p className="font-serif italic text-[11px] text-ink-muted mt-0.5">
                  {item.recordCount} price record{item.recordCount === 1 ? '' : 's'}
                  {' · avg '}
                  ${item.avgPrice.toFixed(2)}
                </p>
              </div>
              <TrendBadge pct={item.priceChangePct} />
            </div>

            {householdId && (
              <PriceHistoryChart
                itemId={item.itemId}
                itemName={item.itemName}
                itemEmoji=""
                householdId={householdId}
                isPremium={isPremium}
              />
            )}
          </motion.div>
        ))}

      </div>
    </main>
  )
}
